import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, orderBy, query, Timestamp, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { getDeflectionStats, getRecentResolvedDeflections, DeflectionStats } from "@/lib/firestore";
import { formatDistanceToNow } from "date-fns";
import { Spinner } from "@/components/shared";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { Deflection, Ticket, TicketPriority } from "@/types";

interface AggregatedStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  byCategory: Record<string, number>;
  byPriority: Record<TicketPriority, number>;
  byAgent: Record<string, { resolved: number; totalMs: number; count: number }>;
  byHour: number[];
  daily: { date: string; count: number }[];
  slaCompliance: Record<TicketPriority, { ontime: number; breached: number }>;
  avgFirstResponseMs: number;
  avgResolutionMs: number;
  resolutionsWithCsat: number;
  csatSum: number;
  triageConfidenceAvg: number;
  triagedCount: number;
}

function emptyStats(): AggregatedStats {
  return {
    total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0,
    byCategory: {}, byPriority: { P1: 0, P2: 0, P3: 0, P4: 0 } as Record<TicketPriority, number>,
    byAgent: {}, byHour: new Array(24).fill(0),
    daily: [],
    slaCompliance: { P1: { ontime: 0, breached: 0 }, P2: { ontime: 0, breached: 0 }, P3: { ontime: 0, breached: 0 }, P4: { ontime: 0, breached: 0 } },
    avgFirstResponseMs: 0, avgResolutionMs: 0,
    resolutionsWithCsat: 0, csatSum: 0,
    triageConfidenceAvg: 0, triagedCount: 0,
  };
}

function aggregate(tickets: (Ticket & { csat?: number })[]): AggregatedStats {
  const s = emptyStats();
  const dailyMap: Record<string, number> = {};
  const now = Date.now();
  let resolutionsTotal = 0;
  let resolutionsCount = 0;

  for (const t of tickets) {
    s.total++;
    if (t.status === "open") s.open++;
    else if (t.status === "in_progress") s.inProgress++;
    else if (t.status === "resolved") s.resolved++;
    else if (t.status === "closed") s.closed++;

    if (t.category) s.byCategory[t.category] = (s.byCategory[t.category] ?? 0) + 1;
    if (t.priority) s.byPriority[t.priority] = (s.byPriority[t.priority] ?? 0) + 1;

    if (t.assignedTo) {
      const a = s.byAgent[t.assignedTo] ??= { resolved: 0, totalMs: 0, count: 0 };
      a.count++;
      if (t.status === "resolved" || t.status === "closed") a.resolved++;
    }

    const created = t.createdAt?.toDate?.();
    if (created) {
      const hr = created.getHours();
      s.byHour[hr]++;
      const day = created.toISOString().slice(0, 10);
      dailyMap[day] = (dailyMap[day] ?? 0) + 1;
    }

    if (t.priority && t.slaBreach) {
      const breachedAt = t.slaBreach.toMillis();
      const resolvedAt = t.resolvedAt?.toMillis();
      if (resolvedAt) {
        if (resolvedAt <= breachedAt) s.slaCompliance[t.priority].ontime++;
        else s.slaCompliance[t.priority].breached++;
      } else if (now > breachedAt) {
        s.slaCompliance[t.priority].breached++;
      }
    }

    if (t.resolvedAt && t.createdAt) {
      const ms = t.resolvedAt.toMillis() - t.createdAt.toMillis();
      resolutionsTotal += ms;
      resolutionsCount++;
    }

    if (t.triageConfidence) {
      s.triagedCount++;
      s.triageConfidenceAvg += t.triageConfidence;
    }

    if (typeof t.csat === "number") {
      s.csatSum += t.csat;
      s.resolutionsWithCsat++;
    }
  }

  if (s.triagedCount) s.triageConfidenceAvg = s.triageConfidenceAvg / s.triagedCount;
  if (resolutionsCount) s.avgResolutionMs = resolutionsTotal / resolutionsCount;

  const sortedDays = Object.entries(dailyMap).sort(([a], [b]) => a.localeCompare(b));
  s.daily = sortedDays.slice(-14).map(([date, count]) => ({ date, count }));

  return s;
}

function formatHours(ms: number): string {
  if (!ms) return "—";
  const hours = ms / (1000 * 60 * 60);
  return hours < 1 ? `${Math.round(hours * 60)}m` : `${hours.toFixed(1)}h`;
}

function MiniBarRow({ label, count, max, color }: { label: string; count: number; max: number; color: string }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
      <span style={{ fontSize: 12, color: "var(--text)", minWidth: 110 }}>{label}</span>
      <div className="pbar" style={{ flex: 1 }}>
        <div className="pfill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span style={{ fontSize: 11, color: "var(--text2)", minWidth: 24, textAlign: "right" }}>{count}</span>
    </div>
  );
}

function AnalyticsCard({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="card card-body"><h3 style={{ marginBottom: 14 }}>{title}</h3>{children}</div>;
}

const CATEGORY_COLORS: Record<string, string> = {
  "Software & Apps": "var(--blue)", "Network & VPN": "var(--gem)",
  "Hardware": "var(--orange)", "Account & Access": "var(--purple)",
  "Email / M365": "var(--green-t)", "Other": "var(--text3)",
};

export default function Analytics() {
  useDocumentTitle("Analytics");
  const [stats, setStats] = useState<AggregatedStats | null>(null);
  const [deflections, setDeflections] = useState<DeflectionStats | null>(null);
  const [recentDeflections, setRecentDeflections] = useState<Deflection[]>([]);

  useEffect(() => {
    const since = Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
    getDocs(query(
      collection(db, "tickets"),
      where("createdAt", ">=", since),
      orderBy("createdAt", "asc")
    )).then((snap) => {
      const tickets = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Ticket & { csat?: number }));
      setStats(aggregate(tickets));
    }).catch(() => setStats(emptyStats()));

    getDeflectionStats().then(setDeflections).catch(() => {});
    getRecentResolvedDeflections(25).then(setRecentDeflections).catch(() => {});
  }, []);

  const kpis = useMemo(() => {
    if (!stats) return [];
    const slaOntime = (Object.values(stats.slaCompliance) as { ontime: number; breached: number }[])
      .reduce((acc, c) => acc + c.ontime, 0);
    const slaTotal = slaOntime + (Object.values(stats.slaCompliance) as { ontime: number; breached: number }[])
      .reduce((acc, c) => acc + c.breached, 0);
    const slaPct = slaTotal > 0 ? Math.round((slaOntime / slaTotal) * 100) : null;
    const csatAvg = stats.resolutionsWithCsat ? (stats.csatSum / stats.resolutionsWithCsat).toFixed(1) : "—";
    return [
      { label: "Total Tickets",     val: String(stats.total),                       sub: "last 30 days",       color: "var(--blue)" },
      { label: "Open",              val: String(stats.open + stats.inProgress),     sub: `${stats.open} new`,  color: "var(--orange-t)" },
      { label: "Avg Resolution",    val: formatHours(stats.avgResolutionMs),        sub: `${stats.resolved} resolved`, color: "var(--blue)" },
      { label: "CSAT Score",        val: csatAvg === "—" ? "—" : `${csatAvg}/5`,    sub: `${stats.resolutionsWithCsat} ratings`, color: "var(--orange-t)" },
      { label: "SLA Compliance",    val: slaPct !== null ? `${slaPct}%` : "—",      sub: `${slaTotal} measured`, color: "var(--green-t)" },
    ];
  }, [stats]);

  if (!stats) {
    return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><Spinner size={28} /></div>;
  }

  if (stats.total === 0) {
    return (
      <div style={{ flex: 1, padding: 20, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", color: "var(--text2)" }}>
        <h1>Analytics</h1>
        <p style={{ fontSize: 14, marginTop: 8 }}>No tickets in the last 30 days yet. Once tickets start flowing, charts and KPIs populate here automatically.</p>
      </div>
    );
  }

  const maxCategory = Math.max(...Object.values(stats.byCategory), 1);
  const maxAgent = Math.max(...Object.values(stats.byAgent).map((a) => a.resolved), 1);
  const maxHour = Math.max(...stats.byHour, 1);

  return (
    <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>
      <h1 style={{ marginBottom: 3 }}>Analytics</h1>
      <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 18 }}>Helpdesk performance — last 30 days</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8, marginBottom: 16 }}>
        {kpis.map((k) => (
          <div key={k.label} className="card card-body" style={{ padding: 13 }}>
            <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 4, fontWeight: 500 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: -.5, color: k.color }}>{k.val}</div>
            <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <AnalyticsCard title="Volume by Category">
          {Object.entries(stats.byCategory).sort(([, a], [, b]) => b - a).map(([cat, count]) => (
            <MiniBarRow key={cat} label={cat} count={count} max={maxCategory} color={CATEGORY_COLORS[cat] ?? "var(--text3)"} />
          ))}
          {Object.keys(stats.byCategory).length === 0 && (
            <div style={{ fontSize: 12, color: "var(--text2)" }}>No categorized tickets yet.</div>
          )}
        </AnalyticsCard>

        <AnalyticsCard title="By Priority">
          {(["P1", "P2", "P3", "P4"] as TicketPriority[]).map((p) => (
            <MiniBarRow key={p} label={p} count={stats.byPriority[p]} max={Math.max(...Object.values(stats.byPriority), 1)}
              color={p === "P1" ? "var(--red-t)" : p === "P2" ? "var(--orange-t)" : p === "P3" ? "var(--blue-t)" : "var(--text3)"} />
          ))}
        </AnalyticsCard>

        <AnalyticsCard title="Agent Workload">
          {Object.keys(stats.byAgent).length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text2)" }}>No assignments yet.</div>
          ) : (
            Object.entries(stats.byAgent).map(([uid, a]) => (
              <MiniBarRow key={uid} label={uid.slice(0, 8) + "…"} count={a.resolved} max={maxAgent} color="var(--blue)" />
            ))
          )}
        </AnalyticsCard>

        <AnalyticsCard title="SLA Compliance by Priority">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {(["P1", "P2", "P3", "P4"] as TicketPriority[]).map((p) => {
              const c = stats.slaCompliance[p];
              const total = c.ontime + c.breached;
              const pct = total > 0 ? Math.round((c.ontime / total) * 100) : null;
              const bg = p === "P1" ? "var(--red-l)" : p === "P2" ? "var(--orange-l)" : p === "P3" ? "var(--blue-l)" : "var(--gray)";
              const color = p === "P1" ? "var(--red-t)" : p === "P2" ? "var(--orange-t)" : p === "P3" ? "var(--blue-t)" : "var(--text)";
              return (
                <div key={p} style={{ textAlign: "center", padding: 10, background: bg, borderRadius: 8 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color }}>{pct !== null ? `${pct}%` : "—"}</div>
                  <div style={{ fontSize: 10, color }}>{p}</div>
                  <div style={{ fontSize: 10, color: "var(--text3)" }}>{total} measured</div>
                </div>
              );
            })}
          </div>
        </AnalyticsCard>

        <AnalyticsCard title="Submission Hour of Day">
          <svg viewBox="0 0 320 60" style={{ width: "100%", height: 60 }}>
            {stats.byHour.map((count, i) => {
              const intensity = count / maxHour;
              const opacity = 0.1 + intensity * 0.9;
              return <rect key={i} x={i * 13} y={5} width={11} height={12} rx={2} fill="var(--blue)" opacity={opacity} />;
            })}
            {[0, 6, 12, 18, 23].map((h) => (
              <text key={h} x={h * 13 + 5} y={30} fontSize={9} fill="var(--text3)" textAnchor="middle">
                {h === 0 ? "12a" : h === 12 ? "12p" : h > 12 ? `${h - 12}p` : `${h}a`}
              </text>
            ))}
          </svg>
        </AnalyticsCard>

        <AnalyticsCard title="AI Triage Performance">
          {stats.triagedCount > 0 ? (
            <>
              <MiniBarRow label="Avg confidence" count={Math.round(stats.triageConfidenceAvg)} max={100} color="var(--gem)" />
              <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 8 }}>
                <strong>{stats.triagedCount}</strong> of {stats.total} tickets ({Math.round(stats.triagedCount / stats.total * 100)}%) auto-triaged.
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: "var(--text2)" }}>No triaged tickets in this window.</div>
          )}
        </AnalyticsCard>

        <AnalyticsCard title="Deflection — last 30 days">
          {!deflections ? (
            <Spinner size={16} />
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text2)" }}>Resolved without ticket</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "var(--gem)" }}>{deflections.resolvedLast30}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text2)" }}>Deflection rate</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "var(--gem)" }}>
                    {deflections.deflectionRate !== null ? `${deflections.deflectionRate}%` : "—"}
                  </div>
                </div>
              </div>
              <MiniBarRow label="Knowledge Base"  count={deflections.byChannel.kb}     max={Math.max(deflections.byChannel.kb, deflections.byChannel.aiChat, 1)} color="var(--blue)" />
              <MiniBarRow label="AI Assistant"    count={deflections.byChannel.aiChat} max={Math.max(deflections.byChannel.kb, deflections.byChannel.aiChat, 1)} color="var(--gem)" />
            </>
          )}
        </AnalyticsCard>
      </div>

      {/* Recent deflections — who got resolved without filing a ticket */}
      {recentDeflections.length > 0 && (
        <AnalyticsCard title={`Recent Deflections (${recentDeflections.length})`}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--gray)" }}>
                  {["When", "User", "Issue", "Channel"].map((h) => (
                    <th key={h} style={{ fontSize: 10, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .3, padding: "6px 10px", textAlign: "left", borderBottom: "0.5px solid var(--border)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentDeflections.map((d) => (
                  <tr key={d.id} style={{ borderBottom: "0.5px solid var(--border)" }}>
                    <td style={{ padding: "8px 10px", color: "var(--text2)", whiteSpace: "nowrap" }}>
                      {d.resolvedAt ? formatDistanceToNow(d.resolvedAt.toDate(), { addSuffix: true }) : "—"}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      <div style={{ fontWeight: 500 }}>{d.userDisplayName}</div>
                      <div style={{ fontSize: 11, color: "var(--text2)", fontFamily: "var(--font-mono)" }}>{d.userEmail}</div>
                    </td>
                    <td style={{ padding: "8px 10px", maxWidth: 360 }}>
                      <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.issueTitle || "(no title)"}</div>
                      {d.issueDescription && (
                        <div style={{ fontSize: 11, color: "var(--text2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.issueDescription}</div>
                      )}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      <span className="badge" style={{ background: d.channel === "ai-chat" ? "var(--gem-l)" : "var(--blue-l)", color: d.channel === "ai-chat" ? "var(--gem-t)" : "var(--blue-t)" }}>
                        {d.channel === "ai-chat" ? "AI" : "KB"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AnalyticsCard>
      )}
    </div>
  );
}
